/* 
  File: TextAreaResize.tsx
  Description: A custom text area component with resizing functionality and styling options.
  TODO: 
  - Add the placeholder color
*/
import { getTokens } from '@my/ui'
import { bodyFont } from '@my/ui/src/config/fonts'
import React, { forwardRef, useCallback, useEffect, useRef } from 'react'
import TextareaAutosize, { TextareaAutosizeProps } from 'react-textarea-autosize'
import { styled } from 'tamagui'

interface TextAreaResizeProps extends Omit<TextareaAutosizeProps, 'onChange'> {
  value: string
  onChange: (value: string) => void
  maxRows?: number
  minRows?: number
  onHeightChange?: (height: number, meta: { rowHeight: number }) => void
  cacheMeasurements?: boolean
  fontSize?: number
  fontBold?: boolean
  singleLine?: boolean
  letterSpacing?: number
}

const TextAreaResizeBase = forwardRef<HTMLTextAreaElement, TextAreaResizeProps>(
  ({ value, onChange, maxRows, minRows, onHeightChange, cacheMeasurements, fontSize, fontBold, singleLine, letterSpacing, ...props }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null)

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        let newValue = event.target.value
        if (singleLine) {
          // Remove any newline characters if singleLine is true
          newValue = newValue.replace(/\n/g, '')
        }
        onChange(newValue)
      },
      [onChange, singleLine]
    )

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (singleLine && (event.key === 'Enter' || event.key === 'Tab')) {
          event.preventDefault()
          textareaRef.current?.blur()
        }
      },
      [singleLine]
    )

    useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(textareaRef.current)
        } else {
          ref.current = textareaRef.current
        }
      }
    }, [ref])

    props.style = {
      ...props.style,
      fontFamily: bodyFont.family,
      lineHeight: 1.3,
      fontSize: fontSize ?? 15,
      fontWeight: fontBold ? '700':'400',
      letterSpacing: letterSpacing ?? 0,
    }

    return (
      <TextareaAutosize
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        maxRows={maxRows}
        minRows={minRows}
        onHeightChange={onHeightChange}
        cacheMeasurements={cacheMeasurements}
        {...props}
      />
    )
  }
)

TextAreaResizeBase.displayName = 'TextAreaResizeBase'

// Create a styled version of TextAreaResizeBase
export const TextAreaResize = styled(TextAreaResizeBase, {
  name: 'TextAreaResize',
  padding: '$2.5',
  borderRadius: '$2',
  borderWidth: 1,
  borderColor: '$borderColor',
  backgroundColor: '$color1',
  color: '$color',
  outlineWidth: 0,
  // Add focus styles
  hoverStyle: {
    borderColor: '$borderColorHover',
  },
  focusStyle: {
    borderColor: '$borderColorFocus',
  },

  // Add placeholder styles
  variants: {
    placeholderColor: {
      ':placeholder': (color: string) => ({
        '::placeholder': {
          color: '$color5',
        },
      }),
    },
  },
})
